import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Components
import Header from './components/Header';
import InfoBar from './components/InfoBar';
import Sidebar from './components/Sidebar';

// Pages
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentsV4 from './pages/v4/DocumentsV4';
import DocumentsListV4 from './pages/v4/DocumentsListV4';
import DocumentsUploadV4 from './pages/v4/DocumentsUploadV4';
import DocumentsSearchV4 from './pages/v4/DocumentsSearchV4';
import DocumentsSettingsV4 from './pages/v4/DocumentsSettingsV4';
import DocumentsAssistantV4 from './pages/v4/DocumentsAssistantV4';
import DocumentsImportV4 from './pages/v4/DocumentsImportV4';
import DocumentsChatLogsV4 from './pages/v4/DocumentsChatLogsV4';
import Products from './pages/Products';
import Chat from './pages/Chat';
import Configurations from './pages/Configurations';
import AhriData from './pages/AhriData';
import AhriAssistant from './pages/AhriAssistant';
import AhriSolr from './pages/AhriSolr';
import Coupons from './pages/Coupons';
import Login from './pages/Login';
import AIImageMaskingSettings from './pages/aiImageMasking/Settings';
import TryBeforeYouBuy from './pages/aiImageMasking/TryBeforeYouBuy';
import SwatchesV2 from './pages/aiImageMasking/SwatchesV2';
import UserGallery from './pages/aiImageMasking/UserGallery';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileSidebarOpen(false);
      } else {
        setIsSidebarCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggleMobileSidebar = () => {
    if (!isMobile) {
      setIsSidebarCollapsed((prev) => !prev);
      return;
    }
    setMobileSidebarOpen((prev) => !prev);
  };

  const handleNavigate = () => {
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100vh' }}>
      <InfoBar />
      <Header onToggleSidebar={handleToggleMobileSidebar} isSidebarOpen={mobileSidebarOpen} />
      <div className="d-flex flex-grow-1 position-relative" style={{ minHeight: 0 }}>
        <Sidebar
          collapsed={isSidebarCollapsed}
          onToggleCollapsed={(next) => setIsSidebarCollapsed(next)}
          mobileVisible={!isMobile || mobileSidebarOpen}
          onNavigate={handleNavigate}
        />
        {isMobile && mobileSidebarOpen && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
            style={{ zIndex: 900 }}
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
        <main
          className="flex-grow-1 p-4"
          style={{ overflowY: 'auto', minWidth: 0 }}
          onClick={() => {
            if (isMobile && mobileSidebarOpen) {
              setMobileSidebarOpen(false);
            }
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
};

// App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <div className="App">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Documents />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/v4/documents" element={<Navigate to="/v4/documents/list" replace />} />
              <Route path="/v4/documents/list" element={<ProtectedRoute><MainLayout><DocumentsListV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/upload" element={<ProtectedRoute><MainLayout><DocumentsUploadV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/search" element={<ProtectedRoute><MainLayout><DocumentsSearchV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/assistant" element={<ProtectedRoute><MainLayout><DocumentsAssistantV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/settings" element={<ProtectedRoute><MainLayout><DocumentsSettingsV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/import" element={<ProtectedRoute><MainLayout><DocumentsImportV4 /></MainLayout></ProtectedRoute>} />
              <Route path="/v4/documents/chat-logs" element={<ProtectedRoute><MainLayout><DocumentsChatLogsV4 /></MainLayout></ProtectedRoute>} />
              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Products />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/chat"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Chat />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configurations"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Configurations />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriData />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/assistant"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriAssistant />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ahri/solr"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AhriSolr />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="/ai-tryout-studio" element={<Navigate to="/ai-tryout-studio/try" replace />} />
              <Route path="/ai-image-masking/*" element={<Navigate to="/ai-tryout-studio/try" replace />} />
              <Route
                path="/ai-tryout-studio/settings"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AIImageMaskingSettings />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/swatches-v2"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SwatchesV2 />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/gallery"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <UserGallery />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-tryout-studio/try"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TryBeforeYouBuy />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coupons"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Coupons />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
             
            </Routes>
          </div>
        </AuthProvider>
        </Router>
      </QueryClientProvider>
  );
}

export default App;
